import React, { useState, useRef, useEffect } from 'react';
import { FaRobot, FaPaperPlane, FaTimes } from 'react-icons/fa';
import { useTranslation } from "react-i18next";
import { SiGoogleassistant } from 'react-icons/si';
import { getAuthToken } from '../../util/auth';
import { getApiChara } from '../../util/getApi';
import classes from './AIChatBot.module.css';

const AIChatBot = ({ context = "" }) => {
    const { t } = useTranslation();
    const [isOpen, setIsOpen] = useState(false);
    const [isMinimized, setIsMinimized] = useState(false);
    const [messages, setMessages] = useState([
        { type: 'ai', text: 'Hello! I am Dr. Tech Buddy, your AI Medical Assistant. How can I help you today?' }
    ]);
    const [input, setInput] = useState('');
    const [isLoading, setIsLoading] = useState(false);

    // Dynamic sizing and position
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [size, setSize] = useState({ width: 330, height: 480 });
    const [isDragging, setIsDragging] = useState(false);
    const [isResizing, setIsResizing] = useState(false);

    const dragItem = useRef();
    const startPos = useRef({ x: 0, y: 0 });
    const startSize = useRef({ width: 0, height: 0 });
    const messageEndRef = useRef(null);

    const toggleChat = () => setIsOpen(!isOpen);

    useEffect(() => {
        if (messageEndRef.current && !isMinimized) {
            messageEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, isOpen, isMinimized]);

    const handleSend = async () => {
        if (!input.trim() || isLoading) return;

        const userText = input;
        setInput('');
        setMessages(prev => [...prev, { type: 'user', text: userText }]);
        setIsLoading(true);

        const token = getAuthToken();
        const apiChar = getApiChara();

        try {
            const response = await fetch(`${apiChar}/ai/ask`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Bearer ${token}`
                },
                body: JSON.stringify({
                    question: userText,
                    context: context
                })
            });

            if (response.ok) {
                const data = await response.json();
                if (data.status === "SUCCESS") {
                    setMessages(prev => [...prev, { type: 'ai', text: data.aiResponse }]);
                } else {
                    setMessages(prev => [...prev, { type: 'ai', text: 'Sorry, I encountered an error: ' + (data.error || 'Unknown error') }]);
                }
            } else {
                setMessages(prev => [...prev, { type: 'ai', text: 'Sorry, I am unable to connect to the AI service right now.' }]);
            }
        } catch (err) {
            console.error('Chat AI Error:', err);
            setMessages(prev => [...prev, { type: 'ai', text: 'Connection failed. Please check your network.' }]);
        } finally {
            setIsLoading(false);
        }
    };

    // Drag & Resize Logic
    const onMouseDownDrag = (e) => {
        if (e.target.closest(`.${classes.closeBtn}`)) return;
        setIsDragging(true);
        startPos.current = {
            x: e.clientX - position.x,
            y: e.clientY - position.y
        };
    };

    const onMouseDownResize = (e) => {
        e.stopPropagation();
        setIsResizing(true);
        startPos.current = { x: e.clientX, y: e.clientY };
        startSize.current = { width: size.width, height: size.height };
    };

    useEffect(() => {
        const onMouseMove = (e) => {
            if (isDragging) {
                const newX = e.clientX - startPos.current.x;
                const newY = e.clientY - startPos.current.y;
                setPosition({ x: newX, y: newY });
            } else if (isResizing) {
                const dx = startPos.current.x - e.clientX;
                const dy = startPos.current.y - e.clientY;
                setSize({
                    width: Math.max(250, startSize.current.width + dx),
                    height: Math.max(350, startSize.current.height + dy)
                });
            }
        };

        const onMouseUp = () => {
            setIsDragging(false);
            setIsResizing(false);
        };

        if (isDragging || isResizing) {
            window.addEventListener('mousemove', onMouseMove);
            window.addEventListener('mouseup', onMouseUp);
        }

        return () => {
            window.removeEventListener('mousemove', onMouseMove);
            window.removeEventListener('mouseup', onMouseUp);
        };
    }, [isDragging, isResizing]);

    return (
        <div className={classes.chatContainer}>
            {isOpen && (
                <div
                    className={`${classes.chatWindow} ${isMinimized ? classes.chatWindowMinimized : ''}`}
                    style={{
                        transform: `translate(${position.x}px, ${position.y}px)`,
                        width: `${size.width}px`,
                        height: `${size.height}px`,
                        transition: (isDragging || isResizing) ? 'none' : 'transform 0.1s ease-out, width 0.2s, height 0.2s'
                    }}
                >
                    {!isMinimized && <div className={classes.resizeHandle} onMouseDown={onMouseDownResize} />}

                    <div className={classes.header} onMouseDown={onMouseDownDrag} style={{ cursor: isDragging ? 'grabbing' : 'grab' }}>
                        <div className={classes.headerTitle}>
                            <div className={classes.roboIconSmall}>
                                <FaRobot size={18} />
                            </div>
                            <div className={classes.headerText}>
                                <span>Dr. AI Assistant</span>
                                {!isMinimized && <span>Powered by Dr. Tech Buddy AI</span>}
                            </div>
                        </div>
                        <div className="d-flex align-items-center gap-2">
                            <div className={classes.closeBtn} onClick={toggleChat} title="Close">
                                <FaTimes />
                            </div>
                        </div>
                    </div>

                    {!isMinimized && (
                        <>
                            <div className={classes.messageArea}>
                                {messages.map((msg, idx) => (
                                    <div key={idx} className={`${classes.message} ${msg.type === 'user' ? classes.userMessage : classes.aiMessage}`}>
                                        {msg.text}
                                    </div>
                                ))}
                                {isLoading && (
                                    <div className={`${classes.message} ${classes.aiMessage}`}>
                                        <div className={classes.loading}>
                                            <div className={classes.dot}></div>
                                            <div className={classes.dot}></div>
                                            <div className={classes.dot}></div>
                                        </div>
                                    </div>
                                )}
                                <div ref={messageEndRef} />
                            </div>

                            <div className={classes.sourceInfo}>
                                <SiGoogleassistant className="me-1" /> Dr. Tech Buddy Assistant
                            </div>

                            <div className={classes.inputArea}>
                                <div className={classes.inputWrapper}>
                                    <input
                                        type="text"
                                        placeholder={t("Ask Dr. AI Assistant...")}
                                        value={input}
                                        onChange={(e) => setInput(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && handleSend()}
                                        disabled={isLoading}
                                    />
                                </div>
                                <button
                                    className={classes.sendBtn}
                                    onClick={handleSend}
                                    disabled={!input.trim() || isLoading}
                                >
                                    <FaPaperPlane size={14} />
                                </button>
                            </div>
                        </>
                    )}
                </div>
            )}

            <div className={classes.fab} onClick={toggleChat} title="Ask Medical AI">
                <FaRobot />
            </div>
        </div>
    );
};

export default AIChatBot;
